import { useEffect, useRef, useState } from 'react';
import { getWhatsAppQR, getWhatsAppEstado, desconectarWhatsApp, enviarWhatsApp } from '../services/api';
import Modal from './ui/Modal';

export interface DestinatarioAcceso {
  id: number;
  nombre: string;
  email: string;
  passwordTemporal: string;
  telefono: string | null;
}

interface Props {
  destinatarios: DestinatarioAcceso[];
  onClose: () => void;
}

function buildMensaje(d: DestinatarioAcceso): string {
  return `Hola ${d.nombre}, te enviamos tus datos de acceso al portal de seguros:\n\nUsuario: ${d.email}\nContraseña: ${d.passwordTemporal}\n\nIngresa en: https://crm.verdeantequeradigital.com\n\nPor seguridad te recomendamos cambiar tu contraseña al ingresar.`;
}

type WaState = 'checking' | 'disconnected' | 'waiting_qr' | 'connected';

export default function WhatsAppAccesoModal({ destinatarios, onClose }: Props) {
  const [waState, setWaState] = useState<WaState>('checking');
  const [phone, setPhone] = useState('');
  const [qr, setQr] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; failed: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const checkEstado = async () => {
    try {
      const res = await getWhatsAppEstado();
      if (res.data.connected) { setWaState('connected'); setPhone(res.data.phone ?? ''); stopPoll(); }
      else setWaState('disconnected');
    } catch { setWaState('disconnected'); }
  };

  useEffect(() => {
    checkEstado();
    return () => stopPoll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConectar = () => {
    setWaState('waiting_qr');
    setQr('');
    setErrorMsg('');
    pollRef.current = setInterval(async () => {
      try {
        const res = await getWhatsAppQR();
        if (res.data.connected) { stopPoll(); setWaState('connected'); setPhone(res.data.phone ?? ''); }
        else if (res.data.qr) setQr(res.data.qr);
      } catch { stopPoll(); setWaState('disconnected'); }
    }, 3000);
  };

  const handleDesconectar = async () => {
    stopPoll();
    try { await desconectarWhatsApp(); } catch { /* ignore */ }
    setWaState('disconnected'); setQr(''); setPhone('');
  };

  const handleEnviar = async () => {
    setSending(true); setErrorMsg(''); setProgress(null);
    try {
      const mensajes = destinatarios
        .filter(d => d.telefono)
        .map(d => ({ telefono: d.telefono!, texto: buildMensaje(d) }));
      const res = await enviarWhatsApp(mensajes);
      setProgress({ sent: res.data.sent ?? mensajes.length, failed: res.data.failed ?? 0 });
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Error al enviar mensajes');
    } finally { setSending(false); }
  };

  const conTelefono = destinatarios.filter(d => d.telefono).length;

  return (
    <Modal title="Enviar acceso por WhatsApp" onClose={onClose}>
      <div className="space-y-4">
        {/* Estado chip */}
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium ${
            waState === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${waState === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
            {waState === 'connected' ? `Conectado${phone ? ` (${phone})` : ''}` :
             waState === 'waiting_qr' ? 'Esperando QR...' :
             waState === 'checking'   ? 'Verificando...' : 'Desconectado'}
          </span>
          {waState === 'connected' && (
            <button onClick={handleDesconectar} className="text-xs text-gray-500 hover:text-red-600 underline">
              Desconectar
            </button>
          )}
          {waState === 'disconnected' && (
            <button onClick={handleConectar}
              className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">
              Conectar WhatsApp
            </button>
          )}
        </div>

        {/* QR */}
        {waState === 'waiting_qr' && (
          <div className="flex flex-col items-center gap-2 py-4">
            {qr ? (
              <>
                <p className="text-sm text-gray-600">Escanea este código QR con tu WhatsApp</p>
                <img src={qr} alt="WhatsApp QR" className="w-56 h-56 border rounded-lg" />
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                Generando QR...
              </div>
            )}
          </div>
        )}

        {/* Credentials card — always visible once destinatarios loaded */}
        {destinatarios.length === 1 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 space-y-1.5 text-sm">
            <p className="font-semibold text-blue-800">{destinatarios[0].nombre}</p>
            <p className="text-blue-700">Usuario: <span className="font-mono">{destinatarios[0].email}</span></p>
            <p className="text-blue-700">Contraseña: <span className="font-mono font-bold">{destinatarios[0].passwordTemporal}</span></p>
            {!destinatarios[0].telefono && (
              <p className="text-orange-600 text-xs">Sin teléfono — agrega uno en Editar usuario para enviar por WA</p>
            )}
            <button
              onClick={() => {
                const msg = buildMensaje(destinatarios[0]);
                navigator.clipboard.writeText(msg);
              }}
              className="mt-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
            >
              Copiar mensaje
            </button>
          </div>
        )}

        {/* Destinatarios + preview */}
        {waState === 'connected' && !progress && (
          <>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Agentes ({destinatarios.length}) — {conTelefono} con teléfono
              </p>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {destinatarios.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">{d.nombre}</p>
                      <p className="text-xs text-gray-500 truncate">{d.email}</p>
                    </div>
                    <span className={`ml-2 flex-shrink-0 text-xs ${d.telefono ? 'text-gray-600' : 'text-red-400'}`}>
                      {d.telefono || 'sin teléfono'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Mensaje template</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 leading-relaxed whitespace-pre-line">
                {`Hola [Nombre], te enviamos tus datos de acceso al portal de seguros:\n\nUsuario: [Email]\nContraseña: [PasswordTemporal]\n\nIngresa en: crm.verdeantequeradigital.com\n\nPor seguridad te recomendamos cambiar tu contraseña al ingresar.`}
              </div>
            </div>

            {errorMsg && <p className="text-red-500 text-sm">{errorMsg}</p>}

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button onClick={onClose}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleEnviar} disabled={sending || conTelefono === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-60 flex items-center gap-2">
                {sending && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {sending ? 'Enviando...' : `Enviar acceso a ${conTelefono} agente${conTelefono !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}

        {/* Resultado */}
        {progress && (
          <div className="text-center py-6 space-y-3">
            <div className="text-4xl">✅</div>
            <p className="text-lg font-semibold text-gray-800">Accesos enviados</p>
            <p className="text-sm text-gray-600">
              Enviados: <span className="text-green-600 font-medium">{progress.sent}</span> &nbsp;·&nbsp;
              Fallidos: <span className="text-red-500 font-medium">{progress.failed}</span>
            </p>
            <button onClick={onClose}
              className="mt-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">
              Cerrar
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
